import { Component, ViewChild } from '@angular/core';
import { AccessManager } from '../../components/ui/access-manager/access-manager';
import { UIErrorHandler } from '../../core/uiErrorHandler';
import { EditUserComponent } from '../../components/user/edit-user/edit-user.component';
import { UserTableViewComponent } from '../../components/user/user-table-view/user-table-view.component';
import { FormMode, UserRoleHierarchy } from '../../core/constants';
import { UIHelpers } from '../../core/ui-helpers';
import { StorageService } from '../../core/storage/storageService';
import { SearchFilters } from '../../models/search/searchFilters';
import { User } from '../../models/users/user';
import { BasePage } from '../BasePage';
import { EditUserInfoDialogComponent } from '../../components/user/edit-user/edit-user-dialog/edit-user-info-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomSnackbarComponent } from '../../components/ui/snackbar/custom-snackbar.component';
import { IconType } from '../../../../../../libs/Shared/components/icon/icon';
import { TableView } from '../../components/ui/filterable-table/tableView';

@Component({
  selector: 'app-users-page',
  templateUrl: './users-page.component.html',
  standalone: false,
})
export class UsersPageComponent extends BasePage {
  @ViewChild('userTable') userTableControl: UserTableViewComponent | undefined;
  @ViewChild('newUserForm') userForm: EditUserComponent | undefined;
  editing: boolean = false;
  namespace: string = AccessManager.AUTH_SELECT_ALL_USERS;

  public get formMode(): typeof FormMode {
    return FormMode;
  }

  IconType = IconType;

  groups: string[] = [
    'Swipesum Sales',
    'Swipesum Success',
    'Swipesum Managers',
    'Site Admins',
  ];

  tableView: any;
  tableViewOptions: any = {
    isAdmin: true,
    selectAction: 'none',
    accountId: undefined,
    merchantId: undefined,
  };

  selectedUser: Partial<User> | undefined;
  selectedGroup: string | undefined;

  addingUser: boolean = false;
  editingUser: boolean = false;
  editingGroup: boolean = false;

  constructor(
    private uiErrorHandler: UIErrorHandler,
    private access: AccessManager,
    private storage: StorageService,
    public editUserDialog: MatDialog,
    private router: Router,
    private snackBar: MatSnackBar,
  ) {
    super();

    BasePage.userService.getUserGroups().then((groups: string[]) => {

      this.groups = groups;
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    this.tableView = new TableView(UserTableViewComponent);

    this.access
      .getUserAccess([
        AccessManager.AUTH_SELECT_ALL_USERS,
        AccessManager.AUTH_MANAGE_ALL_USERS,
        AccessManager.AUTH_IMPERSONATE_USER,
      ])
      .then((accessRecord) => {
        this.tableViewOptions.allowEditing =
          accessRecord[AccessManager.AUTH_MANAGE_ALL_USERS];
        this.tableViewOptions.allowImpersonation =
          accessRecord[AccessManager.AUTH_IMPERSONATE_USER];
        if (this.storage.getCurrentUser().originalRole == 'portal.merchant') {
          this.tableViewOptions.isAdmin = false;
          this.tableViewOptions.accountId =
            this.storage.getSelectedAccount()?.id;
          this.tableViewOptions.merchantId =
            this.storage.getSelectedLocation()?.id;
        }
        this.userTableControl?.setOptions(this.tableViewOptions);
      });

    this.selectedUser = this.storage.getSelectedUser();
  }

  ngOnInitComplete() {}

  /* Disabled for now -- replaced by gear icon
  handleRecordSelected(user:User) {
    this.editingUser = true;
    this.addingUser = false;
    this.selectedUser = user;
  }
  */

  handleUserSelected(userId: string) {
    BasePage.userService
      .getUserById(userId)
      .then((user: User) => {
        if (
          UserRoleHierarchy[
            user.originalRole as keyof typeof UserRoleHierarchy
          ] <=
          UserRoleHierarchy[
            this.storage.getCurrentUser()
              ?.originalRole as keyof typeof UserRoleHierarchy
          ]
        ) {
          this.selectedUser = user;
          this.editingUser = true;
        } else {
          this.snackBar.openFromComponent(CustomSnackbarComponent, {
            duration: 3000,
            panelClass: 'snackbar-warning',
            data: {
              message: 'You do not have permission to edit this user.',
              fontSize: '1.5em',
              iconSize: '50px',
            },
          });
        }
      })
      .catch((e) => {
        this.uiErrorHandler.handleError(e);
      });
  }

  handleUserSaved(data: any) {
    //user:Partial<User>
    this.selectedUser = undefined;
    this.editingUser = false;
    this.addingUser = false;

    // NOTE!!!! This is actually calling FilterableTableComponent.reload(),
    // not TableViewAbstractComponent().reload()
    // but Javascript doesn't understand this
    this.userTableControl?.reload('', new SearchFilters({}), -1, 10);
  }

  handleUserCancelled() {
    this.selectedUser = undefined;
    this.editingUser = false;
    this.addingUser = false;

    this.storage.removeSelectedUser();
  }

  handleUserGroupCancelled() {
    this.selectedGroup = undefined;
    this.editingGroup = false;

    UIHelpers.scrollToTop();
  }

  btnAddUser_click() {
    this.editingUser = false;

    let ref = this.editUserDialog.open(EditUserInfoDialogComponent, {
      disableClose: true,
      data: {
        merchantId: undefined,
      },
    });

    this.router.events.subscribe(() => {
      ref.close();
    });

    ref.afterClosed().subscribe((resp) => {
      if (resp?.error) {
        this.snackBar.openFromComponent(CustomSnackbarComponent, {
          duration: 5000,
          panelClass: 'snackbar-error',
          data: {
            message: 'There was an error adding the user',
            fontSize: '1.5em',
            iconSize: '50px',
          },
        });
      } else {
        this.userTableControl?.reload('', new SearchFilters({}), -1, 10);
      }
    });
  }

  btnCancel_click() {
    this.handleUserCancelled();
  }

  btnSelectGroup_click(groupName: string) {
    this.selectedGroup = groupName;
    this.editingGroup = true;
  }

  btnCancelGroup_click(groupName: string) {
    this.editingGroup = false;
  }
}
